import {
  useEffect,
  useState
} from 'react'

import {
  motion,
  AnimatePresence
} from 'framer-motion'

import {
  FaPlus,
  FaTrash,
  FaEdit,
  FaCheck,
  FaTimes,
  FaGripVertical,
  FaCrown,
  FaJava
} from 'react-icons/fa'

import {
  SiReact, SiMongodb, SiHtml5, SiJavascript, SiNodedotjs, SiExpress,
  SiTailwindcss, SiFigma, SiCanva, SiPython, SiGithub, SiGit, SiDocker,
  SiFirebase, SiMysql, SiPostgresql, SiTypescript, SiCss
} from 'react-icons/si'

const ICON_MAP = {
  'React': <SiReact />,
  'React.js': <SiReact />,
  'React JS': <SiReact />,
  'MongoDB': <SiMongodb />,
  'HTML5': <SiHtml5 />,
  'HTML': <SiHtml5 />,
  'CSS3': <SiCss />,
  'CSS': <SiCss />,
  'JavaScript': <SiJavascript />,
  'JS': <SiJavascript />,
  'Node.js': <SiNodedotjs />,
  'Node': <SiNodedotjs />,
  'Node JS': <SiNodedotjs />,
  'Express': <SiExpress />,
  'Express.js': <SiExpress />,
  'Tailwind CSS': <SiTailwindcss />,
  'Tailwind': <SiTailwindcss />,
  'Figma': <SiFigma />,
  'Canva': <SiCanva />,
  'Python': <SiPython />,
  'Java': <FaJava />,
  'GitHub': <SiGithub />,
  'Git': <SiGit />,
  'Docker': <SiDocker />,
  'Firebase': <SiFirebase />,
  'MySQL': <SiMysql />,
  'PostgreSQL': <SiPostgresql />,
  'TypeScript': <SiTypescript />,
  'TS': <SiTypescript />
}

import {
  TOOL_ICONS,
  TOOL_CATS,
  useAdmin
} from '../../../context/AdminContext'

import {
  AdminCard,
  BtnPrimary,
  BtnOutline,
  ColorPicker
} from './AdminUI'

import {
  getTools,
  addTool,
  updateTool,
  deleteTool
} from '../../../api/portfolioApi'

// ─────────────────────────────────────────────
// Colors
// ─────────────────────────────────────────────
const COLOR_HEX = {
  blue: '#3B82F6',
  purple: '#8B5CF6',
  cyan: '#06B6D4',
  green: '#10B981',
  orange: '#F59E0B',
  yellow: '#EAB308',
  pink: '#EC4899',
  red: '#EF4444'
}

// ─────────────────────────────────────────────
// Empty Form
// ─────────────────────────────────────────────
const emptyForm = {
  name: '',
  icon: '⚡',
  color: 'blue',
  category: 'Tool',
  percentage: 80
}

// ─────────────────────────────────────────────
// Tool Row
// ─────────────────────────────────────────────
function ToolRow({
  tool,
  onEdit,
  onDelete
}) {

  const [confirmDel, setConfirmDel] =
    useState(false)

  const hex =
    COLOR_HEX[tool.color]
    || COLOR_HEX.blue

  return (

    <motion.div

      layout

      initial={{
        opacity: 0,
        y: 8
      }}

      animate={{
        opacity: 1,
        y: 0
      }}

      exit={{
        opacity: 0,
        x: -20
      }}

      className="
        flex
        items-center
        gap-3
        px-4
        py-3
        rounded-xl
        border
        border-white/5
        bg-white/[0.02]
      "
    >

      {/* Drag */}
      <FaGripVertical
        size={10}
        className="
          text-slate-700
          flex-shrink-0
        "
      />

      {/* Icon */}
      <span
        className="
          text-xl
          w-7
          text-center
          flex-shrink-0
        "
        style={{ color: hex }}
      >
        {ICON_MAP[tool.name] || tool.icon}
      </span>

      {/* Name */}
      <div className="flex-1 min-w-0">

        <span
          className="
            font-display
            font-semibold
            text-sm
          "
          style={{
            color: hex
          }}
        >
          {tool.name}
        </span>

        <span
          className="
            ml-2
            font-mono
            text-xs
            text-slate-600
          "
        >
          {tool.category}
        </span>

      </div>

      {/* Dot */}
      <span
        className="
          w-2.5
          h-2.5
          rounded-full
          flex-shrink-0
        "
        style={{
          background: hex
        }}
      />

      {/* Actions */}
      <div
        className="
          flex
          items-center
          gap-1.5
          flex-shrink-0
        "
      >

        {confirmDel ? (

          <>

            <span
              className="
                text-xs
                text-red-400
                font-mono
              "
            >
              Delete?
            </span>

            <motion.button

              onClick={() =>
                onDelete(tool.id)
              }

              whileTap={{
                scale: 0.9
              }}

              className="
                w-6
                h-6
                rounded-lg
                bg-red-500/10
                border
                border-red-500/20
                flex
                items-center
                justify-center
                text-red-400
              "
            >

              <FaCheck size={9} />

            </motion.button>

            <motion.button

              onClick={() =>
                setConfirmDel(false)
              }

              whileTap={{
                scale: 0.9
              }}

              className="
                w-6
                h-6
                rounded-lg
                bg-white/5
                border
                border-white/10
                flex
                items-center
                justify-center
                text-slate-400
              "
            >

              <FaTimes size={9} />

            </motion.button>

          </>

        ) : (

          <>

            <motion.button

              onClick={() =>
                onEdit(tool)
              }

              whileTap={{
                scale: 0.9
              }}

              className="
                w-6
                h-6
                rounded-lg
                bg-blue-500/10
                border
                border-blue-500/20
                flex
                items-center
                justify-center
                text-blue-400
              "
            >

              <FaEdit size={9} />

            </motion.button>

            <motion.button

              onClick={() =>
                setConfirmDel(true)
              }

              whileTap={{
                scale: 0.9
              }}

              className="
                w-6
                h-6
                rounded-lg
                bg-white/5
                border
                border-white/10
                flex
                items-center
                justify-center
                text-slate-500
              "
            >

              <FaTrash size={9} />

            </motion.button>

          </>
        )}

      </div>

    </motion.div>
  )
}

// ─────────────────────────────────────────────
// Tool Form
// ─────────────────────────────────────────────
function ToolForm({
  initial,
  onSave,
  onCancel,
  title
}) {

  const [form, setForm] =
    useState(initial)

  const updateField = (
    key,
    value
  ) => {

    setForm(prev => ({
      ...prev,
      [key]: value
    }))
  }

  return (

    <div
      className="
        p-5
        rounded-2xl
        border
        border-blue-500/15
        bg-blue-500/[0.04]
        mb-5
      "
    >

      <h4
        className="
          font-display
          font-semibold
          text-sm
          text-white
          mb-4
          flex
          items-center
          gap-2
        "
      >

        <span className="text-base">
          {ICON_MAP[form.name] || form.icon}
        </span>

        {title}

      </h4>

      {/* Icons */}
      <div className="mb-4">


        <div
          className="
            flex
            flex-wrap
            gap-2
          "
        >

          {['React', 'Node.js', 'MongoDB', 'HTML5', 'CSS3', 'JavaScript', 'TypeScript', 'Tailwind CSS', 'Figma', 'Python', 'Java', 'GitHub', 'Git', 'Docker', 'Firebase', 'PostgreSQL', 'MySQL', 'Express'].map(icon => (

            <button

              key={icon}

              type="button"

              onClick={() =>
                updateField(
                  'icon',
                  icon
                )
              }

              className={`
                w-9
                h-9
                text-lg
                rounded-xl
                flex
                items-center
                justify-center
                transition-all

                ${form.icon === icon

                  ? `
                    bg-blue-500/25
                    border-2
                    border-blue-500/60
                    scale-110
                  `

                  : `
                    bg-white/5
                    border
                    border-white/10
                  `
                }
              `}
            >

              {ICON_MAP[icon] || icon}

            </button>
          ))}

        </div>

      </div>

      {/* Inputs */}
      <div
        className="
          grid
          grid-cols-2
          gap-3
          mb-4
        "
      >

        {/* Name */}
        <div>

          <label
            className="
              block
              font-mono
              text-xs
              text-slate-500
              mb-1.5
              tracking-widest
              uppercase
            "
          >
            Tool Name *
          </label>

          <input

            value={form.name}

            onChange={(e) =>
              updateField(
                'name',
                e.target.value
              )
            }

            maxLength={40}

            className="
              w-full
              bg-white/5
              border
              border-white/10
              rounded-xl
              px-4
              py-2.5
              text-sm
              text-slate-200
            "
          />

        </div>

        {/* Category */}
        <div>

          <label
            className="
              block
              font-mono
              text-xs
              text-slate-500
              mb-1.5
              tracking-widest
              uppercase
            "
          >
            Category
          </label>

          <select

            value={form.category}

            onChange={(e) =>
              updateField(
                'category',
                e.target.value
              )
            }

            className="
              w-full
              bg-white/5
              border
              border-white/10
              rounded-xl
              px-4
              py-2.5
              text-sm
              text-slate-200
            "
          >

            {TOOL_CATS.map(cat => (

              <option
                key={cat}
                value={cat}
              >
                {cat}
              </option>
            ))}

          </select>

        </div>

        {/* Percentage */}
        <div>

          <label
            className="
              block
              font-mono
              text-xs
              text-slate-500
              mb-1.5
              tracking-widest
              uppercase
            "
          >
            Percentage (0-100)
          </label>

          <input
            type="number"
            min="0"
            max="100"
            value={form.percentage || 80}

            onChange={(e) =>
              updateField(
                'percentage',
                parseInt(e.target.value) || 0
              )
            }

            className="
              w-full
              bg-white/5
              border
              border-white/10
              rounded-xl
              px-4
              py-2.5
              text-sm
              text-slate-200
            "
          />

        </div>

      </div>

      {/* Color */}
      <div className="mb-5">

        <label
          className="
            block
            font-mono
            text-xs
            text-slate-500
            mb-2
            tracking-widest
            uppercase
          "
        >
          Accent Color
        </label>

        <ColorPicker
          value={form.color}
          onChange={(value) =>
            updateField(
              'color',
              value
            )
          }
        />

      </div>

      {/* Preview */}
      <div
        className="
          mb-4
          flex
          items-center
          gap-2
        "
      >

        <span
          className="
            font-mono
            text-xs
            text-slate-600
          "
        >
          Preview →
        </span>

        <div

          className="
            flex
            items-center
            gap-2
            px-4
            py-2
            rounded-xl
            text-xs
            font-display
            font-semibold
            border
          "

          style={{
            background:
              `${COLOR_HEX[form.color] || COLOR_HEX.blue}14`,

            borderColor:
              `${COLOR_HEX[form.color] || COLOR_HEX.blue}30`,

            color:
              COLOR_HEX[form.color]
              || COLOR_HEX.blue
          }}
        >

          <span>{ICON_MAP[form.name] || form.icon}</span>

          <span>
            {form.name || 'Tool Name'}
          </span>

        </div>

      </div>

      {/* Buttons */}
      <div className="flex gap-3">

        <BtnPrimary

          onClick={() => {
            if (form.name.trim()) {
              onSave(form)
            }
          }}

          disabled={!form.name.trim()}
        >

          <FaCheck size={10} />

          Save Tool

        </BtnPrimary>

        <BtnOutline onClick={onCancel}>

          <FaTimes size={10} />

          Cancel

        </BtnOutline>

      </div>

    </div>
  )
}

// ─────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────
export default function SkillsEditor({
  onSaved
}) {

  // ───────────────────────────────────────────
  // Context
  // ───────────────────────────────────────────
  const {
    refreshPortfolio
  } = useAdmin()

  // ───────────────────────────────────────────
  // State
  // ───────────────────────────────────────────
  const [tools, setTools] =
    useState([])

  const [loading, setLoading] =
    useState(false)

  const [error, setError] =
    useState('')

  const [adding, setAdding] =
    useState(false)

  const [editingId, setEditingId] =
    useState(null)

  const [filterCat, setFilterCat] =
    useState('All')

  // ───────────────────────────────────────────
  // Load Tools
  // ───────────────────────────────────────────
  useEffect(() => {
    loadTools()
  }, [])

  const loadTools = async () => {

    try {

      setLoading(true)

      setError('')

      const data =
        await getTools()

      setTools(data || [])

    } catch (err) {

      console.error(err)

      setError(
        'Failed to load tools'
      )

    } finally {

      setLoading(false)
    }
  }

  // ───────────────────────────────────────────
  // Categories
  // ───────────────────────────────────────────
  const cats = [

    'All',

    ...Array.from(
      new Set(
        tools.map(
          tool => tool.category
        )
      )
    ).sort()
  ]

  const visibleTools =
    filterCat === 'All'

      ? tools

      : tools.filter(
        tool =>
          tool.category
          === filterCat
      )

  // ───────────────────────────────────────────
  // Add
  // ───────────────────────────────────────────
  const handleAdd =
    async (form) => {

      try {

        setLoading(true)

        setError('')

        const created =
          await addTool(form)

        setTools(prev => [
          ...prev,
          created.tool
        ])

        // Refresh homepage
        await refreshPortfolio()

        setAdding(false)

        onSaved?.(
          'Tool added!'
        )

      } catch (err) {

        console.error(err)

        setError(
          err.message
          || 'Failed to add tool'
        )

      } finally {

        setLoading(false)
      }
    }

  // ───────────────────────────────────────────
  // Edit
  // ───────────────────────────────────────────
  const handleEdit =
    async (form) => {

      try {

        setLoading(true)

        setError('')

        await updateTool(
          editingId,
          form
        )

        setTools(prev =>
          prev.map(tool =>
            tool.id === editingId

              ? {
                ...tool,
                ...form
              }

              : tool
          )
        )

        // Refresh homepage
        await refreshPortfolio()

        setEditingId(null)

        onSaved?.(
          'Tool updated!'
        )

      } catch (err) {

        console.error(err)

        setError(
          err.message
          || 'Failed to update tool'
        )

      } finally {

        setLoading(false)
      }
    }

  // ───────────────────────────────────────────
  // Delete
  // ───────────────────────────────────────────
  const handleDelete =
    async (id) => {

      try {

        setLoading(true)

        setError('')

        await deleteTool(id)

        setTools(prev =>
          prev.filter(
            tool => tool.id !== id
          )
        )

        // Refresh homepage
        await refreshPortfolio()

        onSaved?.(
          'Tool removed.'
        )

      } catch (err) {

        console.error(err)

        setError(
          err.message
          || 'Failed to delete tool'
        )

      } finally {

        setLoading(false)
      }
    }

  // ───────────────────────────────────────────
  // UI
  // ───────────────────────────────────────────
  return (

    <AdminCard

      title="Manage Skills"

      subtitle={`
        ${tools.length}
        tools
      `}

      action={
        <BtnPrimary
          onClick={() => {
            setAdding(true)
            setEditingId(null)
          }}
          small
        >

          <FaPlus size={9} />

          Add Skill

        </BtnPrimary>
      }
    >

      {/* Error */}
      {error && (

        <div
          className="
            mb-4
            rounded-xl
            border
            border-red-500/20
            bg-red-500/10
            px-4
            py-3
            text-sm
            text-red-400
          "
        >
          {error}
        </div>
      )}

      {/* Add */}
      <AnimatePresence>

        {adding && (

          <motion.div

            initial={{
              opacity: 0,
              height: 0
            }}

            animate={{
              opacity: 1,
              height: 'auto'
            }}

            exit={{
              opacity: 0,
              height: 0
            }}

            className="
              overflow-hidden
            "
          >

            <ToolForm

              initial={emptyForm}

              onSave={handleAdd}

              onCancel={() =>
                setAdding(false)
              }

              title="Add New Skill"

            />

          </motion.div>
        )}

      </AnimatePresence>

      {/* Filter */}
      {cats.length > 2 && (

        <div
          className="
            flex
            flex-wrap
            gap-1.5
            mb-4
          "
        >

          {cats.map(cat => (

            <button

              key={cat}

              onClick={() =>
                setFilterCat(cat)
              }

              className={`
                px-3
                py-1
                rounded-lg
                font-mono
                text-xs

                ${filterCat === cat

                  ? `
                    bg-blue-500/15
                    border
                    border-blue-500/25
                    text-blue-400
                  `

                  : `
                    bg-white/4
                    border
                    border-white/8
                    text-slate-500
                  `
                }
              `}
            >

              {cat}

            </button>
          ))}

        </div>
      )}

      {/* List */}
      <div className="space-y-2">

        <AnimatePresence>

          {visibleTools.length === 0 ? (

            <div
              className="
                text-center
                py-10
                font-mono
                text-xs
                text-slate-600
              "
            >
              No tools yet.
            </div>

          ) : (

            visibleTools.map(tool => (

              editingId === tool.id ? (

                <motion.div

                  key={`edit-${tool.id}`}

                  layout

                  initial={{
                    opacity: 0
                  }}

                  animate={{
                    opacity: 1
                  }}

                  exit={{
                    opacity: 0
                  }}
                >

                  <ToolForm

                    initial={{
                      name:
                        tool.name,

                      icon:
                        tool.icon,

                      color:
                        tool.color,

                      category:
                        tool.category
                    }}

                    onSave={handleEdit}

                    onCancel={() =>
                      setEditingId(null)
                    }

                    title={`
                      Editing:
                      ${tool.name}
                    `}

                  />

                </motion.div>

              ) : (

                <ToolRow

                  key={tool.id}

                  tool={tool}

                  onEdit={(tool) => {

                    setEditingId(tool.id)

                    setAdding(false)
                  }}

                  onDelete={handleDelete}

                />
              )
            ))
          )}

        </AnimatePresence>

      </div>

      {/* Preview */}
      {tools.length > 0 && (

        <div
          className="
            mt-6
            pt-5
            border-t
            border-white/5
          "
        >

          <p
            className="
              font-mono
              text-xs
              text-slate-600
              mb-3
            "
          >
            Live preview
          </p>

          <div
            className="
              flex
              flex-wrap
              gap-2
            "
          >

            {tools.map(tool => {

              const hex =
                COLOR_HEX[tool.color]
                || COLOR_HEX.blue

              return (

                <span

                  key={tool.id}

                  className="
                    flex
                    items-center
                    gap-2
                    px-3
                    py-2
                    rounded-xl
                    text-xs
                    font-display
                    font-semibold
                    border
                  "

                  style={{
                    background:
                      `${hex}10`,

                    borderColor:
                      `${hex}25`,

                    color: hex
                  }}
                >

                  {tool.icon}

                  {tool.name}

                </span>
              )
            })}

          </div>

        </div>
      )}

    </AdminCard>
  )
}